import { getPDFBase64 } from "./generateW8BENXML";
import { formatDateForXML, normalizeDateToSlash } from "../../../Utility/utility";
import { W9Service } from "../../../services/W9Service";
import { W9FormModel } from "../../../Models/W9Models";
import { getUSStateCode } from "../../../utils/formUtils";

// Document type mapping with TypeID and Type
const documentTypeMapping: Record<string, { typeId: string; type: string }> = {
  partnership_agreement: {
    typeId: "74B29767-B166-49A1-90C6-04D919FFF021",
    type: "Partnership Agreement",
  },
  trust_deed: {
    typeId: "E0E34437-CC13-487C-863E-248204E61086",
    type: "Trust Deed or Certified Extracts",
  },
  certificate_registration: {
    typeId: "F0B2D5EC-5C24-4FE5-A571-2610610FA3D1",
    type: "Certificate of Registration",
  },
  certificate_incorporation: {
    typeId: "F113A468-ADE7-4C20-82C4-33A496456F54",
    type: "Certificate of Incorporation",
  },
  armed_forces_card: { typeId: "5EBD9B1F-BEB2-4BB5-BCB9-35C105F278A5", type: "Armed Forces Card" },
  limitations_benefits: {
    typeId: "5309E91F-6B24-4183-9C7F-3A062658E625",
    type: "Limitations of Benefits Statement",
  },
  election_form: {
    typeId: "DB6649B1-F4BE-41D5-AAAD-4588B253C39D",
    type: "Election form - line 9 option",
  },
  public_register: {
    typeId: "58740ED7-F2EF-481D-B0FA-4703F257B1C6",
    type: "Public Register Extracts",
  },
  driving_license: { typeId: "9FECCC5A-4CC3-46C2-AB7D-53FB2E3DB0B8", type: "Driving License" },
  driverLicense: { typeId: "9FECCC5A-4CC3-46C2-AB7D-53FB2E3DB0B8", type: "Driving License" },
  state_eu: { typeId: "D525BDE1-E0A2-4F2A-B2CE-5AD75383264E", type: "State of the EU" },
  email: { typeId: "C2676B9E-BCFA-497E-8588-6B8B1DA00914", type: "eMail" },
  retroactive_statement: {
    typeId: "3DC45284-ACA4-494B-AA1F-6EBA8736F9E1",
    type: "Retroactive Statement",
  },
  birth_certificate: { typeId: "70612B92-D0F3-4347-9CC3-912B9597C800", type: "Birth Certificate" },
  passport: { typeId: "70825FED-AEBF-4EB8-949D-998D69D718F9", type: "Passport" },
  withholding_statement: {
    typeId: "872ACA95-E43E-4EFA-A494-9A4A97356365",
    type: "Withholding Statement",
  },
  email_alt: { typeId: "325FFDDE-2558-4E32-B7DA-A67435BF59F9", type: "eMail" },
  other: { typeId: "E697B03A-91D1-4CEB-8EC4-A7A4E53E5B30", type: "Other" },
  hybrid_status: {
    typeId: "5A447556-7724-484B-A6D4-BAE392B8FF22",
    type: "Hybrid Status - Supporting Documentation",
  },
  notional_principle: {
    typeId: "BB810C57-8652-4431-BEB2-BE50057C17DC",
    type: "Notional principle contracts statement",
  },
  connected_documentation: {
    typeId: "ACCDB44A-2518-4B06-95C2-BF837E64E343",
    type: "Connected documentation",
  },
  national_id_member: {
    typeId: "4EA07713-50FD-4427-AB0A-C3972E3A7BB0",
    type: "National ID Card of A Member",
  },
  form_w4: { typeId: "D9AFD794-205A-45C3-A64A-C5219F20CBB2", type: "Form W-4" },
  proof_residency: {
    typeId: "F1BC6DC5-19C6-441F-A133-CEDE22778968",
    type: "Proof of Residency Document",
  },
  affidavit: { typeId: "B6477F29-2FE4-4CC0-A58E-CF7B91F791E7", type: "Affidavit" },
  national_id: { typeId: "405AE4A8-A1FA-45EB-9449-D1C76EC101FB", type: "National ID Card" },
  effectively_connected: {
    typeId: "BD2028C8-56A5-4404-895A-D36F42535DDB",
    type: "Effectively connected income statement - line 9 option",
  },
  power_attorney: {
    typeId: "59AC92CD-62C8-46DC-BC33-DBAFA0716242",
    type: "Power of attorney statement",
  },
  us_counsel: {
    typeId: "B6A5ABBA-E61E-4C5C-9B43-E051D75A26F6",
    type: "U.S. counsel opinion statement",
  },
  work_visa: { typeId: "1F7944EA-CF76-40FB-B182-E3D1DE59A74A", type: "Work visa" },
  expatriate: { typeId: "7AE8E536-21FD-46A2-93B7-EE99477D7DE2", type: "Expatriate Documentation" },
};

// Helper function to get document type info
const getDocumentTypeInfo = (documentType: string) => {
  // Normalize the document type by removing common prefixes and converting to lowercase
  const normalizedType = documentType
    .toLowerCase()
    .replace(/^idDocument\./, "")
    .replace(/\.pdf$/, "");

  // Try direct match first
  if (documentTypeMapping[normalizedType]) {
    return documentTypeMapping[normalizedType];
  }

  // Try partial matches for common document types
  if (normalizedType.includes("passport")) {
    return documentTypeMapping["passport"];
  }
  if (normalizedType.includes("driver") || normalizedType.includes("license")) {
    return documentTypeMapping["driving_license"];
  }
  if (normalizedType.includes("national") && normalizedType.includes("id")) {
    return documentTypeMapping["national_id"];
  }

  // Default to 'Other' if no match found
  return documentTypeMapping["other"];
};

// Parse address components function
const parseAddressComponents = (addressString: string) => {
  if (!addressString) return { city: "", state: "", postalCode: "" };

  // Split by comma and trim whitespace
  const parts = addressString.split(",").map((part) => part.trim());

  // Initialize result object
  const result = {
    city: "",
    state: "",
    postalCode: "",
  };

  // If we have at least one part, it's the city
  if (parts.length > 0) {
    result.city = parts[0];
  }

  // If we have a second part, it's the state
  if (parts.length > 1) {
    result.state = parts[1];
  }

  // If we have a third part, it's the postal code
  if (parts.length > 2) {
    result.postalCode = parts[2];
  }

  return result;
};

export const generateW9XML = async (inputData: any) => {
  let formModel: W9FormModel;

  // Check if data has already been mapped (isMapDone flag)
  const isMapped = inputData?.isMapDone === true;

  if (inputData instanceof W9FormModel) {
    // If already a form model, use it directly
    formModel = inputData;
  } else if (!isMapped) {
    formModel = W9Service.fromApiResponse(inputData);
    console.log("✅ W9 XML: Field mapping completed");
  } else {
    // Data is already mapped, just convert to form model
    formModel = new W9FormModel(inputData);
  }

  // Helper function to safely get values (excluding "Unknown" values)
  const getValue = (key: string, defaultValue: string = "") => {
    const value = formModel[key] || defaultValue;
    // Filter out "Unknown", "N/A", and similar invalid values
    if (
      value &&
      typeof value === "string" &&
      (value.toLowerCase() === "unknown" ||
        value.toLowerCase() === "n/a" ||
        value.toLowerCase() === "not applicable" ||
        value.toLowerCase() === "none")
    ) {
      return "";
    }
    return value;
  };

  const getBooleanValue = (key: string) => {
    const value = formModel[key];
    if (
      value === true ||
      value === "true" ||
      value === "selected" ||
      value === "yes" ||
      value === "Yes"
    ) {
      return "True";
    }
    return "False";
  };

  Object.keys(formModel)
    .filter((key) => key.includes("Tax_classification") || key.includes("Federal"))
    .forEach((key) => {
      // console.log(`${key}: ${formModel[key]} (type: ${typeof formModel[key]})`);
    });

  // Helper function to determine entity type
  const determineEntityType = () => {
    // Helper function to check if value is properly selected
    const isSelected = (value: any): boolean => {
      if (typeof value === "boolean") return value;
      if (typeof value === "string")
        return (
          value === "selected" ||
          value === "true" ||
          value === "yes" ||
          value === "on" ||
          value === "1"
        );
      if (typeof value === "number") return value === 1;
      return false;
    };

    if (isSelected(formModel["W9-Tax_classification_other"])) {
      return "Other";
    }

    if (isSelected(formModel["W9-Tax_classification_individual_sole_proprietor"])) {
      console.log("✅ SELECTED: Individual/Sole proprietor");
      return "Individual/Sole proprietor";
    }

    if (isSelected(formModel["W9-Tax_classification_c_corporation"])) {
      console.log("✅ SELECTED: C Corporation");
      return "Corporation";
    }

    if (isSelected(formModel["W9-Tax_classification_partnership"])) {
      console.log("✅ SELECTED: Partnership");
      return "Partnership";
    }

    if (isSelected(formModel["W9-Tax_classification_trust_estate"])) {
      console.log("✅ SELECTED: Trust/estate");
      return "Trust/estate";
    }

    if (isSelected(formModel["W9-Tax_classification_llc"])) {
      console.log("✅ SELECTED: Limited liability company");
      return "Limited liability company";
    }

    console.log("❌ NO SELECTION FOUND - Defaulting to Individual/Sole proprietor");
    return ""; // No match — do not send a default value
  };

  // Generate related documents XML
  const generateRelatedDocumentsXml = async () => {
    let relatedDocumentsXml = "";

    // Handle single document case from original code
    if (formModel.idDocumentUrl) {
      const docContent = await getPDFBase64(formModel.idDocumentUrl);
      const docTypeInfo = getDocumentTypeInfo(formModel.idDocumentType || "other");

      relatedDocumentsXml += `
      <RelatedDocument>
        <Type>${docTypeInfo.type}</Type>
        <TypeID>${docTypeInfo.typeId}</TypeID>
        <File>
          <Name>${formModel.idDocumentType || "id_document"}.pdf</Name>
          <Data>${docContent}</Data>
        </File>
      </RelatedDocument>`;
    }

    // Handle multiple documents if provided in docs array
    if (formModel.docs && Array.isArray(formModel.docs)) {
      for (const doc of formModel.docs) {
        if (doc.url) {
          // Make sure to await the PDF base64 conversion
          const docContent = await getPDFBase64(doc.url);
          const docTypeInfo = getDocumentTypeInfo(doc.type || "other");

          relatedDocumentsXml += `
          <RelatedDocument>
            <Type>${docTypeInfo.type}</Type>
            <TypeID>${docTypeInfo.typeId}</TypeID>
            <File>
              <Name>${doc.name || "document.pdf"}</Name>
              <Data>${docContent}</Data>
            </File>
          </RelatedDocument>`;
        }
      }
    }
    // Handle withholding statement document (attached to first form matching first row of excel)
    if (formModel.withholdingStatementUrl) {
      try {
        const whsContent = await getPDFBase64(formModel.withholdingStatementUrl);
        const whsTypeInfo = getDocumentTypeInfo("withholding_statement");

        relatedDocumentsXml += `
      <RelatedDocument>
        <Type>${whsTypeInfo.type}</Type>
        <TypeID>${whsTypeInfo.typeId}</TypeID>
        <File>
          <Name>Withholding Statement.pdf</Name>
          <Data>${whsContent}</Data>
        </File>
      </RelatedDocument>`;
      } catch (err) {
        console.error("Failed to attach Withholding Statement:", err);
      }
    }
    return relatedDocumentsXml;
  };

  const relatedDocumentsXml = await generateRelatedDocumentsXml();

  // Generate original file XML if viewPdf is provided
  const generateOriginalFileXml = async () => {
    const viewPdfUrl = getValue("viewPdf", "");
    if (viewPdfUrl) {
      try {
        const originalFileContent = await getPDFBase64(viewPdfUrl, (formModel as any).fileImportId);
        const fileName = viewPdfUrl.split("/").pop() || "original_document.pdf";

        return `
        <OriginalFile>
          <Name>${fileName}</Name>
          <Data>${originalFileContent}</Data>
        </OriginalFile>`;
      } catch (error) {
        console.error("Error converting original PDF to base64:", error);
        return "<OriginalFile></OriginalFile>";
      }
    }
    return "<OriginalFile></OriginalFile>";
  };

  const originalFileXml = await generateOriginalFileXml();

  // Helper function to check if individual classification is selected
  const isSelected = (value: any): boolean => {
    if (typeof value === "boolean") return value;
    if (typeof value === "string")
      return (
        value === "selected" ||
        value === "true" ||
        value === "yes" ||
        value === "on" ||
        value === "1"
      );
    if (typeof value === "number") return value === 1;
    return false;
  };

  // Determine name fields based on tax classification
  const isIndividual = isSelected(formModel["W9-Tax_classification_individual_sole_proprietor"]);

  let firstName = "";
  let lastName = "";
  let organizationName = "";

  if (isIndividual) {
    // For individuals: use first/last name fields, organization name should be empty
    firstName = getValue("First name and middle name", "");
    lastName = getValue("Last name", "");
    organizationName = ""; // Clear organization name for individuals
  } else {
    // For entities: use organization name, clear first/last name
    firstName = "";
    lastName = "";
    organizationName = getValue("Name of organization", "");
  }

  const submissionDate = formatDateForXML(getValue("Signature_date", ""));
  const determineUSFederalTaxClassification = () => {
    // Use the same isSelected function as entity type
    const isSelected = (value: any): boolean => {
      if (typeof value === "boolean") return value;
      if (typeof value === "string")
        return (
          value === "selected" ||
          value === "true" ||
          value === "yes" ||
          value === "on" ||
          value === "1"
        );
      if (typeof value === "number") return value === 1;
      return false;
    };

    if (isSelected(formModel["W9-Federal_s_corporation"])) {
      return "S-Corporation";
    }

    if (isSelected(formModel["W9-Federal_c_corporation"])) {
      return "C-Corporation";
    }

    if (isSelected(formModel["W9-Federal_partnership"])) {
      return "Partnership";
    }

    console.log("❌ US Federal: No selection found, returning empty string");
    return "";
  };
  const xml = `
<W_9_MAR_2024>
  <UniqueIdentifier>${getValue("UniqueIdentifier")}</UniqueIdentifier>
  <UIDreferenceline>False</UIDreferenceline>
  <Instructor_Identifier>${getValue("entityUniqueIdentifier")}</Instructor_Identifier>
  <BusinessUnit>${getValue("BusinessUnit", "")}</BusinessUnit>
  <ElectronicRecipientStatement>True</ElectronicRecipientStatement>
  <ElectronicRecipientStatementDate>${submissionDate}</ElectronicRecipientStatementDate>
  <Entity></Entity>
  <CommonInfo>
    <EntityTypes>
      <EntityType>${determineEntityType()}</EntityType>
    </EntityTypes>
    // <USFederalTaxClassification>${determineUSFederalTaxClassification()}</USFederalTaxClassification>
    <EntityTypeOther>${getValue("W9-Tax_classification_other_description", "")}</EntityTypeOther>
    <EntityClassification></EntityClassification>
    <InitialEntityTypeSelection></InitialEntityTypeSelection>
    <FATCAEntityType/>
    <Individual>
      <FirstName>${firstName}</FirstName>
      <LastName>${lastName}</LastName>
      <CountryOfBirth/>
      <CountryOfBirthOther/>
      <DateOfBirth/>
      <CountryOfCitizenshipForTax/>
      <CountryOfCitizenshipForTaxOther/>
      <CityTownOfBirth/>
      <PlaceofBirth/>
      <AdditionalTaxJurisdictions/>
    </Individual>
    <Business>
      <Name>${organizationName}</Name>
      <Country>${getValue("Country_of_incorporation/organization", "United States")}</Country>
      <CountryOther/>
      <DoingBusinessAsName>${getValue("W9-Business_name", "")}</DoingBusinessAsName>
      <DREFATCAEntitytype>
        <SCorp>${getBooleanValue("W9-Federal_s_corporation")}</SCorp>
        <CCorp>${getBooleanValue("W9-Federal_c_corporation")}</CCorp>
        <Partnership>${getBooleanValue("W9-Federal_partnership")}</Partnership>
      </DREFATCAEntitytype>
      <IsChapter3_Line3b>${getValue("W9-3B_foreign_partners") === "yes" || getValue("W9-3B_foreign_partners") === "selected" ? "True" : "False"}</IsChapter3_Line3b>
      <JurisdictionTaxCountry/>
      <SponsoringEntityName/>
      <SponsoringEntityGIIN/>
      <TrusteeName/>
      <TrusteeGIIN/>
      <AdditionalTaxJurisdictions>
        <AdditionalTaxJurisdiction>
          <Country>US</Country>
          <CountryOther/>
          <TaxpayerId/>
          <TaxpayerIdType/>
          <FATCAClassification>${getValue("Chapter4Status", "")}</FATCAClassification>
          <SpecifiedPerson/>
          <TINUnavailable/>
          <TINFormatNotAvail/>
        </AdditionalTaxJurisdiction>
      </AdditionalTaxJurisdictions>
      <IsSpecifiedUSPerson>False</IsSpecifiedUSPerson>
      <SpecifiedPersonUS>NA</SpecifiedPersonUS>
      <IsSpecifiedUKPerson/>
      <SpecifiedPersonUK/>
      <IsSpecifiedCRSPerson/>
      <SpecifiedPersonCRS/>
    </Business>
    <USFederalTaxClassifications>
        <USFederalTaxClassification>${determineUSFederalTaxClassification()}</USFederalTaxClassification>
    </USFederalTaxClassifications>
    <PermanentAddress>
      <Line1>${getValue("Permanent residence address", "")}</Line1>
      <Line2>${getValue("Permanent address line 2", "")}</Line2>
      <Line3>${getValue("Permanent address line 3", "")}</Line3>
      <City_Town>${getValue("Permanent address city", "")}</City_Town>
      <State_Province>${getUSStateCode(getValue("Permanent address state USCA", "") || getValue("Permanent address state NonUS", ""))}</State_Province>
      <Country>${getValue("Permanent address country", "United States")}</Country>
      <CountryOther>${getValue("Permanent address other", "")}</CountryOther>
      <Zip_PostalCode>${getValue("Permanent address zip", "")}</Zip_PostalCode>
      <IsCare_of_address>${getValue("Permanent address is care of", "") === "yes" ? "True" : "False"}</IsCare_of_address>
      <IsP_O_Box>${getValue("Permanent address is po box", "") === "yes" ? "True" : "False"}</IsP_O_Box>
      <IsRuralRoute>${getValue("Permanent address is rural route", "") === "yes" ? "True" : "False"}</IsRuralRoute>
    </PermanentAddress>
    <MailingAddress>
      <Line1/>
      <Line2/>
      <Line3/>
      <City_Town/>
      <State_Province/>
      <Country/>
      <CountryOther/>
      <Zip_PostalCode/>
      <IsCare_of_address/>
      <IsP_O_Box/>
      <IsRuralRoute/>
    </MailingAddress>
    <ContactInfo>
      <Email>${getValue("Taxpayer email", "")}</Email>
      <TelephoneCountry/>
      <TelephoneNumber/>
      <CellNumber/>
    </ContactInfo>
    <SubsidiaryInfo>
      <SubsidiaryId/>
      <SubsidiaryName/>
      <SubsidiaryContactName/>
      <SubsidiaryContactTelephone/>
      <SubsidiaryContactEmail/>
    </SubsidiaryInfo>
    <HybridStatus>Not Applicable</HybridStatus>
    <HybridStatusDescription/>
    <HybridStatusSupportingDocumentationAttached>False</HybridStatusSupportingDocumentationAttached>
    <IsOnBehalfOfSomeoneElse>False</IsOnBehalfOfSomeoneElse>
    <Proxy>
      <FirstName>${firstName}</FirstName>
      <LastName>${lastName}</LastName>
      <Capacity/>
      <CapacityOther/>
      <CountryAlternative/>
      <AlterNativeNumber/>
      <CountryAlternativeII/>
      <AlterNativeNumberII/>
    </Proxy>
    <NoUSSiDeclarationConfirmed/>
    <NoUSSiDeclarationPart2_3Skipped>False</NoUSSiDeclarationPart2_3Skipped>
    <IgnoredRules>
      <IgnoredRule>
        <Comment/>
      </IgnoredRule>
      <IgnoredRule>
        <Comment/>
      </IgnoredRule>
    </IgnoredRules>
    <NonUSIndividual_IsClaimingECI/>
    <NonUSIndividual_IsForIndependentPersonalServices/>
    <NonUSIndividual_IsPublicEntertainer/>
    <NonUSIndividual_IsResidentNoSpecificProvision/>
    <NonUSIndividual_IsForDependentPersonalServices/>
    <NonUSIndividual_IsForScholarshipFellowshipAndPersonalServicesIncome/>
    <NonUSIndividual_PartOfWhichNotClaimingTaxTreatyWithholdingExemption/>
    <NonUSIndividual_IsNotClaimingTaxTreatyWithholdingExemption/>
    <NonUSIndividual_IsForScholarshipFellowshipNoPersonalServicesIncome/>
    <NonUSIndividual_IsForOnlyForeignStatusOrTreatyBenefits/>
    <SelectedOptionsonGuide4/>
  </CommonInfo>
  <TaxpayerIdentification>
    <Type>${getValue("Taxpayer type", "")}</Type>
    <GIINAppliedFor>False</GIINAppliedFor>
    <GIINNotApplicable/>
    <SSNOrITIN>${getValue("Social security number", "")}</SSNOrITIN>
    <EIN>${getValue("Employer identification number", "")}</EIN>
    <TINFormatNotAvail>False</TINFormatNotAvail>
    <ForeignTaxpayerCountry/>
    <ForeignTinUnavailable>False</ForeignTinUnavailable>
    <ForeignTinUnavailableReason/>
    <IsAppliedForTIN>${getBooleanValue("Taxpayer applied for")}</IsAppliedForTIN>
    <UnexpectedTINTypeExplanation/>
    <UnexpectedTINTypeExplanationConfirmed/>
    <UnexpectedTINTypeContentBlock/>
    <TINCheck>
      <IsCheckPreformed>False</IsCheckPreformed>
      <IsSuccessful>False</IsSuccessful>
      <Date/>
    </TINCheck>
  </TaxpayerIdentification>
  <SignInfo>
    <IsSigned>${getValue("isSigned", "") === "yes" ? "True" : "False"}</IsSigned>
    <IsModified>${getValue("isModified", "") === "yes" ? "True" : "False"}</IsModified>
    <SignatureUnreadable>${getBooleanValue("Signature_unreadable")}</SignatureUnreadable>
    <By>${getValue("Print_name", "")}</By>
    <Capacity/>
    <Date>${normalizeDateToSlash(getValue("Signature_date", ""))}</Date>
    <DateOnForm>${getValue("Signature_date_on_form", "") || getValue("Signature_date", "")}</DateOnForm>
  </SignInfo>
  <AccoutOutsideUS>False</AccoutOutsideUS>
  <BackupWithholdingExemptionCode>${getValue("W9-Exempt_payee_code", "")}</BackupWithholdingExemptionCode>
  <ExemptPayeeMultiple>${getBooleanValue("Exempt payee multiple")}</ExemptPayeeMultiple>
  <ExemptPayeeMultipleText>${getValue("Exempt payee multiple text", "")}</ExemptPayeeMultipleText>
  <ExemptPayeeUnknown>${getBooleanValue("Exempt payee unknown")}</ExemptPayeeUnknown>
  <FATCAExemptionCode>${getValue("W9-Fatca_exemption_code", "")}</FATCAExemptionCode>
  <FATCAMultiple>${getBooleanValue("FATCA multiple")}</FATCAMultiple>
  <FATCAMultipleText>${getValue("FATCA multiple text", "")}</FATCAMultipleText>
  <FATCAUnknown>${getBooleanValue("FATCA unknown")}</FATCAUnknown>
  <AccountNumbersList>${getValue("W9-Account_numbers", "")}</AccountNumbersList>
  <IsPartIILine2a_CrossedOut></IsPartIILine2a_CrossedOut>
  <IsPartIILine2b_CrossedOut></IsPartIILine2b_CrossedOut>
  <IsPartIILine2c_CrossedOut></IsPartIILine2c_CrossedOut>
  <IsSubjectToBackupWithholding></IsSubjectToBackupWithholding>
  <RequestersNameAndAddress>${getValue("W9-Requester_name_address", "")}</RequestersNameAndAddress>
  <Tag>
    <SubmissionDate></SubmissionDate>
    <IsFromComplyExchangeService>True</IsFromComplyExchangeService>
    <IsFromBureau>False</IsFromBureau>
    <IsPaperForm>False</IsPaperForm>
    <IsE_form>False</IsE_form>
    <IsMobileSubmission>False</IsMobileSubmission>
  </Tag>
  <RelatedDocuments>
    ${relatedDocumentsXml}
  </RelatedDocuments>
  ${originalFileXml}
  <SubstituteFormStatement>The Internal Revenue Service does not require your consent to any provisions of this document other than the certifications required to avoid backup withholding.</SubstituteFormStatement>
</W_9_MAR_2024>`;

  // Log the final XML data
  console.log("🎯 W9 Final XML Data:");
  console.log(xml);
  console.log("🎯 End W9 XML Data");

  return xml;
};
